import { NgClass } from '@angular/common'
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild, computed, inject, signal } from '@angular/core'
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { NotificationChannel } from '@app/modules/database/models/resend-confirmation-pricing'
import { IntegrationService } from '@app/modules/events/services/integration.service'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { take } from 'rxjs'

/** Which pre-approved WhatsApp body is shown in the preview. */
type WhatsappTemplateType = 'confirmation' | 'reminder'

/** Preview language. Independent of the app locale. */
type WhatsappTemplateLang = 'en' | 'ar'

/** One flag inside whatsapp_communication on the persona save payload. */
type WhatsappFlag = 'enable_online' | 'enable_onsite' | 'enable_reminders'

/** One switch row under WhatsApp Integration. */
type WhatsappFlagRow = {
  name: WhatsappFlag
  title: string
  description: string
}

type WhatsappTemplatePreview = {
  lines: string[]
  action: string
}

/**
 * WhatsApp channel inside persona Confirmations & Reminders.
 * Switch values live on the persona form and are sent as whatsapp_communication.
 */
@Component({
  selector: 'ngx-whatsapp-integration',
  templateUrl: './whatsapp-integration.component.html',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, ModalComponent]
})
export class WhatsappIntegrationComponent implements AfterViewInit, OnDestroy {
  /** Persona flags saved as whatsapp_communication. */
  @Input({ required: true }) whatsappCommunication: UntypedFormGroup

  /** Event named in the custom-template Slack request. */
  @Input() event: { name?: string; slug?: string } | null = null

  /** Stable id so each persona form owns its own support dialog. */
  readonly supportModalId = `whatsapp-request-support-${crypto.randomUUID()}`

  /** Online, on-site, and reminder switches. Same track, different flag and copy. */
  readonly flags: WhatsappFlagRow[] = [
    {
      name: 'enable_online',
      title: 'Enable for Online Registration',
      description: 'Sent when a guest completes the online registration form.'
    },
    {
      name: 'enable_onsite',
      title: 'Enable for On-site Registration',
      description: 'Sent when a guest is registered at the venue.'
    },
    {
      name: 'enable_reminders',
      title: 'Enable for Reminders (Inherited from email)',
      description: 'Sent on the schedule defined in Set reminders.'
    }
  ]

  readonly templateType = signal<WhatsappTemplateType>('confirmation')

  readonly templateLang = signal<WhatsappTemplateLang>('en')

  /** True while the custom-template Slack request is in flight. */
  readonly isContactingSupport = signal(false)

  readonly activeTemplate = computed(() => this._templates[this.templateType()][this.templateLang()])

  /**
   * USD per WhatsApp confirmation or reminder. Follows the workspace import-confirmation rate.
   */
  get whatsappMessagePriceUsd(): number {
    return this._auth.importConfirmationUnitPriceUsd[NotificationChannel.WHATSAPP]
  }

  private readonly _modalService = inject(ModalService)
  private readonly _auth = inject(AuthService)
  private readonly _integration = inject(IntegrationService)
  private readonly _alert = inject(AlertService)

  /** Observers that keep each sliding fill aligned after the tab is shown. */
  private readonly _segmentObservers: ResizeObserver[] = []

  @ViewChild('typeSwitch') private readonly _typeSwitch?: ElementRef<HTMLElement>
  @ViewChild('langSwitch') private readonly _langSwitch?: ElementRef<HTMLElement>

  private readonly _templates: Record<WhatsappTemplateType, Record<WhatsappTemplateLang, WhatsappTemplatePreview>> = {
    confirmation: {
      en: {
        lines: ['Hello {{full_name}},', 'Thank you for confirming your attendance at {{event_name}}.', 'Keep this confirmation handy on the day of event.'],
        action: 'Open confirmation'
      },
      ar: {
        lines: ['مرحباً {{full_name}}،', 'تم تأكيد تسجيلك في {{event_name}}.', 'يرجى الاحتفاظ بهذا التأكيد وإظهاره عند الوصول.'],
        action: 'فتح التأكيد'
      }
    },
    reminder: {
      en: {
        lines: ['Hello {{full_name}},', 'This is a reminder regarding your confirmed registration for {{event_name}}.', 'Keep the confirmation handy on the day of the event.'],
        action: 'Open reminder'
      },
      ar: {
        lines: ['مرحباً {{full_name}}،', 'نود تذكيرك بتسجيلك المؤكد في فعالية {{event_name}}.', 'نأمل الاحتفاظ برسالة التأكيد وإبرازها يوم الفعالية.'],
        action: 'فتح التذكير'
      }
    }
  }

  /**
   * Drops the support dialog and the segment observers when the persona form goes away.
   */
  public ngOnDestroy(): void {
    this._segmentObservers.forEach((observer) => observer.disconnect())
    this._modalService.unregister(this.supportModalId)
  }

  /**
   * Form control for one WhatsApp flag on the persona save payload.
   * @param name Flag inside whatsapp_communication
   */
  public whatsappControl(name: WhatsappFlag): UntypedFormControl {
    return this.whatsappCommunication.get(name) as UntypedFormControl
  }

  /**
   * Watches the template and language switches so the sliding fill stays aligned.
   */
  public ngAfterViewInit(): void {
    this._watchSegment(this._typeSwitch, () => this.templateType())
    this._watchSegment(this._langSwitch, () => this.templateLang())
  }

  /**
   * Slides the highlight onto the confirmation or reminder half.
   */
  public selectTemplateType(type: WhatsappTemplateType): void {
    this.templateType.set(type)
    this._placeSegment(this._typeSwitch, type)
  }

  /**
   * Slides the highlight onto the English or Arabic half.
   */
  public selectTemplateLang(lang: WhatsappTemplateLang): void {
    this.templateLang.set(lang)
    this._placeSegment(this._langSwitch, lang)
  }

  /**
   * Keeps the sliding fill on the active half when the switch is shown or resized.
   * @param group Switch that owns the fill
   * @param value Current option for that switch
   */
  private _watchSegment(group: ElementRef<HTMLElement> | undefined, value: () => string): void {
    const root = group?.nativeElement
    if (!root || typeof ResizeObserver === 'undefined') {
      this._placeSegment(group, value())
      return
    }
    const observer = new ResizeObserver(() => this._placeSegment(group, value()))
    observer.observe(root)
    this._segmentObservers.push(observer)
    this._placeSegment(group, value())
  }

  /**
   * Moves the highlight onto one half of a template or language switch.
   * The first placement is instant so the fill does not grow in from zero.
   * @param group Switch that owns the fill
   * @param value Option to cover
   */
  private _placeSegment(group: ElementRef<HTMLElement> | undefined, value: string): void {
    const root = group?.nativeElement
    const button = root?.querySelector<HTMLElement>(`[data-segment="${value}"]`)
    if (!root || !button?.offsetWidth) return
    const thumb = root.querySelector<HTMLElement>('[data-segment-thumb]')
    const placed = root.dataset.placed === 'true'
    root.style.setProperty('--segment-x', `${button.offsetLeft}px`)
    root.style.setProperty('--segment-w', `${button.offsetWidth}px`)
    if (placed) return
    root.dataset.placed = 'true'
    requestAnimationFrame(() => thumb?.setAttribute('data-ready', 'true'))
  }

  public isSupportModalOpen(): boolean {
    return this._modalService.isModalOpen(this.supportModalId)
  }

  public openSupportModal(): void {
    this._openModal(this.supportModalId)
  }

  /**
   * Closes the custom-template support dialog.
   * Used for Cancel and Contact support. The modal X and backdrop already toggle themselves.
   */
  public closeSupportModal(): void {
    this._closeModal(this.supportModalId)
  }

  /**
   * Posts a Slack request for a custom WhatsApp template, then closes the dialog.
   * The message names the current workspace and event.
   */
  public contactSupport(): void {
    if (this.isContactingSupport()) return
    const workspace = this._auth.currentWorkspace
    const payload: { blocks: { type: string; text: { type: string; text: string } }[]; channel?: string } = {
      blocks: [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Workspace:* ${workspace?.name ?? 'Unknown'}\n*Workspace slug:* \`${workspace?.slug ?? ''}\``
          }
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Event:* ${this.event?.name ?? 'Unknown'}\n*Event slug:* \`${this.event?.slug ?? ''}\``
          }
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: 'This workspace is requesting a custom WhatsApp template.'
          }
        }
      ]
    }
    if (environment.production) payload.channel = 'support'
    this.isContactingSupport.set(true)
    this._integration
      .sendSlackMessage(payload, false)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isContactingSupport.set(false)
          this._alert.alert(Alert.SUCCESS, 'Support request sent')
          this.closeSupportModal()
        },
        error: () => this.isContactingSupport.set(false)
      })
  }

  /**
   * Opens a dialog unless it is already open. Toggle flips, so a second call would close it.
   */
  private _openModal(id: string): void {
    if (!this._modalService.isModalOpen(id)) this._modalService.toggleModal(id)
  }

  /**
   * Closes a dialog unless it is already closed.
   */
  private _closeModal(id: string): void {
    if (this._modalService.isModalOpen(id)) this._modalService.toggleModal(id)
  }
}
